/**
 * Authentication Store
 * Zustand store for auth state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthChangeEvent, Session as SupabaseSession } from '@supabase/supabase-js';
import { supabase, getCurrentUser, getCurrentSession, onAuthStateChange } from '@/lib/supabase-client';
import type { User, Session } from '@/types';

function mapSupabaseSession(
  session: SupabaseSession | null,
  userOverride?: User | null
): Session | null {
  if (!session?.access_token || !session.user) {
    return null;
  }

  const user = (userOverride ?? (session.user as unknown as User)) as User;

  return {
    user,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at
      ? new Date(session.expires_at * 1000).toISOString()
      : new Date().toISOString(),
  };
}

function applyAuthSession(session: SupabaseSession | null, userOverride?: User | null) {
  useAuthStore.setState({
    session: mapSupabaseSession(session, userOverride),
    user: session?.user ? ((userOverride ?? session.user) as unknown as User) : null,
    error: null,
    isLoading: false,
    isInitialized: true,
  });
}

let authSubscriptionCleanup: (() => void) | null = null;

export function subscribeAuthStoreToSupabaseAuthChanges(): () => void {
  if (authSubscriptionCleanup) {
    return authSubscriptionCleanup;
  }

  const {
    data: { subscription },
  } = onAuthStateChange((_event: AuthChangeEvent, session: SupabaseSession | null) => {
    applyAuthSession(session);
  });

  authSubscriptionCleanup = () => {
    subscription.unsubscribe();
    authSubscriptionCleanup = null;
  };

  return authSubscriptionCleanup;
}

interface AuthState {
  // State
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Async Actions
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

// Export the type explicitly
export type { AuthState };

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial State
      user: null,
      session: null,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Setters
      setUser: (user) => set({ user }),
      setSession: (session) => set({ session }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      // Initialize auth state
      initialize: async () => {
        set({ isLoading: true });
        try {
          const session = await getCurrentSession();
          if (!session) {
            set({ user: null, session: null, error: null });
            return;
          }

          const user = await getCurrentUser();
          if (!user) {
            set({ user: null, session: null, error: null });
            return;
          }

          applyAuthSession(session, user as unknown as User);
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({ user: null, session: null, error: null });
        } finally {
          set({ isLoading: false, isInitialized: true });
        }
      },

      // Login
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) {
            throw error;
          }

          if (data.session && data.user) {
            applyAuthSession(data.session, data.user as unknown as User);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Logout
      logout: async () => {
        set({ isLoading: true });
        try {
          await supabase.auth.signOut();
          set({
            user: null,
            session: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Logout failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Refresh session
      refreshSession: async () => {
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error) {
            throw error;
          }

          if (data.session && data.user) {
            applyAuthSession(data.session, data.user as unknown as User);
          }
        } catch (error) {
          console.error('Session refresh error:', error);
          // Clear auth state on refresh failure
          set({ user: null, session: null });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        session: state.session,
      }),
    }
  )
);

// Auth state selectors
export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => !!state.session;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectAuthError = (state: AuthState) => state.error;

// Role-based selectors
export const selectUserRole = (state: AuthState) => state.user?.role;
export const selectIsAdmin = (state: AuthState) => state.user?.role === 'admin';
